import { t } from '@hefe/i18n';
import {
  BasketballGame,
  EventReportEventableTypeEnum,
  EventReportGroupableTypeEnum,
  EventReportType,
} from '@hefe/models';
import { BasketballGamesApi, EventReportsSlice } from '@hefe/store';
import { Button, Col, Row, Skeleton, Space, Steps } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import GenerateEventReportWizardGeneralFormComponent, {
  GENERATE_EVENT_REPORT_WIZARD_GENERAL_FORM_ID,
} from '../generate-event-report-wizard-general-form/generate-event-report-wizard-general-form.component';
import GenerateEventReportWizardPregameFormComponent, {
  GENERATE_EVENT_REPORT_WIZARD_PREGAME_FORM_ID,
} from '../generate-event-report-wizard-pregame-form/generate-event-report-wizard-pregame-form.component';
import GenerateEventReportWizardPreviewFormComponent, {
  GENERATE_EVENT_REPORT_WIZARD_PREVIEW_FORM_ID,
} from '../generate-event-report-wizard-preview/generate-event-report-wizard-preview.component';
import GenerateEventReportWizardQuartersFormComponent, {
  GENERATE_EVENT_REPORT_WIZARD_QUARTERS_FORM_ID,
} from '../generate-event-report-wizard-quarters-form/generate-event-report-wizard-quarters-form.component';
import GenerateEventReportWizardSummaryFormComponent, {
  GENERATE_EVENT_REPORT_WIZARD_SUMMARY_FORM_ID,
} from '../generate-event-report-wizard-summary-form/generate-event-report-wizard-summary-form.component';

interface GenerateEventReportWizardComponentProps {
  game: BasketballGame;
  gameId: number;
  eventType: EventReportEventableTypeEnum;
  eventId: number;
  groupType?: EventReportGroupableTypeEnum;
  groupId: number;
}

export default function GenerateEventReportWizardComponent(
  props: GenerateEventReportWizardComponentProps
) {
  const dispatch = useAppDispatch();
  const wizard = useSelector(EventReportsSlice.generateWizardSelector);

  const { data, isFetching } = BasketballGamesApi.useFetchGameInfoQuery(
    props.gameId
  );

  const onClickBack = () => {
    dispatch(
      EventReportsSlice.actions.setCurrentWizardStep(wizard.currentStep - 1)
    );
  };

  const [buttonLoading, setButtonLoading] = useState(false);

  const activeComponent = useMemo(() => {
    if (!data) {
      return;
    }

    if (wizard?.data?.type === EventReportType.POST) {
      switch (wizard.currentStep) {
        case 0:
          return (
            <GenerateEventReportWizardGeneralFormComponent game={props.game} />
          );
        case 1:
          return <GenerateEventReportWizardQuartersFormComponent data={data} />;
        case 2:
          return <GenerateEventReportWizardSummaryFormComponent data={data} />;
        case 3:
          return (
            <GenerateEventReportWizardPreviewFormComponent
              gameId={props.gameId}
              game={props.game}
              eventType={props.eventType}
              eventId={props.eventId}
              groupType={props.groupType}
              groupId={props.groupId}
              setButtonLoading={setButtonLoading}
            />
          );
        default:
          return (
            <GenerateEventReportWizardGeneralFormComponent game={props.game} />
          );
      }
    } else {
      switch (wizard.currentStep) {
        case 0:
          return (
            <GenerateEventReportWizardGeneralFormComponent game={props.game} />
          );
        case 1:
          return <GenerateEventReportWizardPregameFormComponent data={data} />;
        case 2:
          return (
            <GenerateEventReportWizardPreviewFormComponent
              gameId={props.gameId}
              game={props.game}
              eventType={props.eventType}
              eventId={props.eventId}
              groupType={props.groupType}
              groupId={props.groupId}
              setButtonLoading={setButtonLoading}
            />
          );
        default:
          return (
            <GenerateEventReportWizardGeneralFormComponent game={props.game} />
          );
      }
    }
  }, [
    data,
    wizard?.data?.type,
    wizard.currentStep,
    props.game,
    props.gameId,
    props.eventType,
    props.eventId,
    props.groupType,
    props.groupId,
  ]);

  const btnBackDisabled = useMemo(() => {
    return wizard.currentStep === 0;
  }, [wizard.currentStep]);

  const btnNextDisabled = useMemo(() => {
    return wizard.currentStep === 4;
  }, [wizard.currentStep]);

  const btnNextText = useMemo(() => {
    if (wizard.data?.type === EventReportType.POST) {
      switch (wizard.currentStep) {
        case 0:
          return t('game_reports.generate.wizard.steps.general.button_next');
        case 1:
          return t(
            'game_reports.generate.wizard.steps.game_details.button_next'
          );
        case 2:
          return t('game_reports.generate.wizard.steps.summary.button_next');
        case 3:
          return t('game_reports.generate.wizard.steps.preview.button_next');
        default:
          return t('next');
      }
    } else {
      switch (wizard.currentStep) {
        case 0:
          return t('game_reports.generate.wizard.steps.general.button_next');
        case 1:
          return t('game_reports.generate.wizard.steps.pregame.button_next');
        case 2:
          return t('game_reports.generate.wizard.steps.preview.button_next');
        default:
          return t('next');
      }
    }
  }, [wizard.currentStep, wizard.data?.type]);

  const formId = useMemo(() => {
    if (wizard.data?.type === EventReportType.POST) {
      switch (wizard.currentStep) {
        case 0:
          return GENERATE_EVENT_REPORT_WIZARD_GENERAL_FORM_ID;
        case 1:
          return GENERATE_EVENT_REPORT_WIZARD_QUARTERS_FORM_ID;
        case 2:
          return GENERATE_EVENT_REPORT_WIZARD_SUMMARY_FORM_ID;
        case 3:
          return GENERATE_EVENT_REPORT_WIZARD_PREVIEW_FORM_ID;
        default:
          return 'generate-event-report-wizard-general-form';
      }
    } else {
      switch (wizard.currentStep) {
        case 0:
          return GENERATE_EVENT_REPORT_WIZARD_GENERAL_FORM_ID;
        case 1:
          return GENERATE_EVENT_REPORT_WIZARD_PREGAME_FORM_ID;
        case 2:
          return GENERATE_EVENT_REPORT_WIZARD_PREVIEW_FORM_ID;
        default:
          return 'generate-event-report-wizard-general-form';
      }
    }
  }, [wizard.currentStep, wizard.data?.type]);

  return (
    <Skeleton loading={isFetching} active>
      <Row>
        <Col span={24}>
          <Steps current={wizard.currentStep}>
            <Steps.Step
              title={t('game_reports.generate.wizard.steps.general.title')}
            />
            {wizard?.data?.type === EventReportType.POST && (
              <>
                <Steps.Step
                  title={t(
                    'game_reports.generate.wizard.steps.game_details.title'
                  )}
                />
                <Steps.Step
                  title={t('game_reports.generate.wizard.steps.summary.title')}
                />
              </>
            )}
            {wizard?.data?.type === EventReportType.PRE && (
              <Steps.Step
                title={t('game_reports.generate.wizard.steps.pregame.title')}
              />
            )}
            <Steps.Step
              title={t('game_reports.generate.wizard.steps.preview.title')}
            />
          </Steps>
        </Col>
      </Row>
      <Row style={{ padding: 30 }}>
        <Col span={24}>{activeComponent}</Col>
      </Row>
      <Row justify="end">
        <Space size={8}>
          <Button disabled={btnBackDisabled} onClick={() => onClickBack()}>
            {t('back')}
          </Button>
          <Button
            form={formId}
            htmlType="submit"
            disabled={btnNextDisabled}
            loading={buttonLoading}
          >
            {btnNextText}
          </Button>
        </Space>
      </Row>
    </Skeleton>
  );
}
